import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import DOMPurify from "dompurify";
import {
  SWISSTOPO_BASE_LAYERS,
  SWISSTOPO_OVERLAYS,
  SWISSTOPO_ATTRIBUTION,
  SWISS_CENTER,
  SWISS_DEFAULT_ZOOM,
  identifyFeatures,
  fetchHtmlPopup,
} from "../lib/swisstopo";
import "../lib/map.css";

// Default Leaflet icon URLs (fix for webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export const makeRepertoPin = (code) => {
  const prefix = (code || "V")[0] || "V";
  return L.divIcon({
    className: "",
    html: `<div class="reperto-pin ${prefix}">${code || "?"}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Small coloured dot used for imported Google My Maps points. Kept smaller
// than artifact pins (22px vs 32px) so the user's actual catalog stays the
// visual priority. White border for contrast on any base layer.
export const makeMyMapsPin = (color = "#9C5B42") => {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(color) ? color : "#9C5B42";
  return L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${safe};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.35);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
};

const LayerSwitcher = ({ baseId, setBaseId, overlayIds, setOverlayIds, opacities, setOpacity }) => {
  const [collapsed, setCollapsed] = useState(false);
  const toggleOverlay = (id) => {
    setOverlayIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };
  // Group overlays by 'group' field
  const grouped = {};
  SWISSTOPO_OVERLAYS.forEach((l) => {
    grouped[l.group] = grouped[l.group] || [];
    grouped[l.group].push(l);
  });
  const activeCount = overlayIds.length;
  return (
    <div className={`layer-switcher${collapsed ? " collapsed" : ""}`} data-testid="layer-switcher">
      <button
        type="button"
        className="switcher-toggle"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="layer-switcher-toggle"
      >
        <span>Strati{activeCount > 0 ? ` · ${activeCount + 1}` : ""}</span>
        <span className="toggle-icon">▾</span>
      </button>
      <div className="switcher-body">
        <div className="group-title">Sfondo</div>
        {SWISSTOPO_BASE_LAYERS.map((l) => (
          <div key={l.id} className="layer-row">
            <label>
              <input
                type="radio"
                name="base"
                checked={baseId === l.id}
                onChange={() => setBaseId(l.id)}
              />
              <span>{l.label}</span>
            </label>
          </div>
        ))}
        {Object.entries(grouped).map(([group, layers]) => (
          <React.Fragment key={group}>
            <div className="group-title">{group}</div>
            {layers.map((l) => {
              const active = overlayIds.includes(l.id);
              const op = opacities[l.id] ?? l.opacity ?? 1;
              return (
                <div key={l.id} className="layer-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleOverlay(l.id)}
                    />
                    <span>{l.label}</span>
                  </label>
                  {active && (
                    <div className="opacity-row">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(op * 100)}
                        onChange={(e) => setOpacity(l.id, parseInt(e.target.value) / 100)}
                        className="opacity-slider"
                        data-testid={`opacity-${l.id}`}
                      />
                      <span className="font-mono w-9 text-right">{Math.round(op * 100)}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ClickHandler = ({ onClick }) => {
  const map = useMap();
  useMapEvents({
    click(e) { onClick && onClick(e.latlng, map); },
  });
  return null;
};

const FlyTo = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 14, { duration: 0.8 });
    // eslint-disable-next-line
  }, [position?.[0], position?.[1]]);
  return null;
};

export default function SwissTopoMap({
  center = SWISS_CENTER,
  zoom = SWISS_DEFAULT_ZOOM,
  markers = [],          // [{ position: [lat,lng], code, label, popup, onClick }]
  myMapsPoints = [],     // [{ lat, lng, name, description, color, layer_name, layer_id }]
  height = 460,
  showLayerSwitcher = true,
  pickable = false,
  onPick,                // (latlng) => void
  pickedPosition = null, // [lat, lng] to show as draggable preview
  flyTo = null,          // [lat,lng]
  initialOverlays = [],
}) {
  const [baseId, setBaseId] = useState(SWISSTOPO_BASE_LAYERS.find((l) => l.default).id);
  const [overlayIds, setOverlayIds] = useState(initialOverlays);
  // Per-layer opacity (initialized from layer.opacity default)
  const [opacities, setOpacities] = useState(() => {
    const init = {};
    SWISSTOPO_OVERLAYS.forEach((l) => { init[l.id] = l.opacity ?? 1; });
    return init;
  });
  const setOpacity = (id, v) => setOpacities((s) => ({ ...s, [id]: v }));

  // Feature-info popup state
  const [featurePopup, setFeaturePopup] = useState(null); // { latlng, content, loading }

  const base = SWISSTOPO_BASE_LAYERS.find((l) => l.id === baseId);
  const activeOverlays = SWISSTOPO_OVERLAYS.filter((l) => overlayIds.includes(l.id));
  const featureInfoBods = activeOverlays.filter((l) => l.featureInfo).map((l) => l.bodLayerId);

  const handleMapClick = async (latlng, mapInstance) => {
    if (pickable) {
      onPick && onPick(latlng);
      return;
    }
    if (featureInfoBods.length === 0) return;
    const b = mapInstance.getBounds();
    const size = mapInstance.getSize();
    setFeaturePopup({ latlng, content: null, loading: true });
    try {
      const results = await identifyFeatures({
        layerBodIds: featureInfoBods,
        lat: latlng.lat,
        lng: latlng.lng,
        mapBounds: { west: b.getWest(), east: b.getEast(), south: b.getSouth(), north: b.getNorth() },
        mapSize: { x: size.x, y: size.y },
      });
      if (results.length === 0) {
        setFeaturePopup({ latlng, content: "<p>Nessuna informazione in questo punto.</p>", loading: false });
        return;
      }
      const htmlParts = await Promise.all(
        results.slice(0, 5).map(async (r) => {
          const layerId = r.layerBodId || r.layerName;
          const fid = r.featureId || r.id;
          if (!layerId || !fid) return null;
          const html = await fetchHtmlPopup(layerId, fid);
          return html || `<h4>${r.layerName || ""}</h4><p>${(r.properties?.description || "")}</p>`;
        }),
      );
      const combined = htmlParts.filter(Boolean).join('<hr style="border:none;border-top:1px solid rgba(0,0,0,.1);margin:10px 0">');
      setFeaturePopup({ latlng, content: combined || "<p>Nessuna informazione.</p>", loading: false });
    } catch (e) {
      console.error(e);
      setFeaturePopup({ latlng, content: "<p>Errore nel caricamento.</p>", loading: false });
    }
  };

  return (
    <div className="relative" style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          key={base.id}
          url={base.url}
          attribution={SWISSTOPO_ATTRIBUTION}
          maxZoom={19}
        />
        {activeOverlays.map((l) => (
          <TileLayer
            key={l.id}
            url={l.url}
            opacity={opacities[l.id] ?? l.opacity ?? 1}
            attribution={l.attribution}
            maxZoom={19}
          />
        ))}

        {(pickable || featureInfoBods.length > 0) && (
          <ClickHandler onClick={handleMapClick} />
        )}
        {flyTo && <FlyTo position={flyTo} zoom={14} />}

        {featurePopup && (
          <Popup
            position={featurePopup.latlng}
            eventHandlers={{ remove: () => setFeaturePopup(null) }}
            maxWidth={360}
            minWidth={260}
          >
            {featurePopup.loading ? (
              <div className="text-sm py-1">Caricamento informazioni...</div>
            ) : (
              <div
                className="feature-info-popup max-h-[280px] overflow-y-auto pr-1"
                style={{ fontSize: "12px", lineHeight: "1.4" }}
                // SwissTopo returns sanitized HTML for the feature info popup,
                // but we run it through DOMPurify defensively in case the
                // upstream service is ever compromised or changes behaviour.
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(featurePopup.content || "", {
                    ALLOWED_TAGS: [
                      "p", "div", "span", "a", "h1", "h2", "h3", "h4", "h5", "h6",
                      "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td",
                      "b", "strong", "i", "em", "u", "br", "hr", "img",
                    ],
                    ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "style", "class"],
                    ALLOW_DATA_ATTR: false,
                  }),
                }}
              />
            )}
          </Popup>
        )}

        {markers.map((m, i) => (
          <Marker
            key={`${m.code || "m"}-${m.position[0]}-${m.position[1]}-${i}`}
            position={m.position}
            icon={m.code ? makeRepertoPin(m.code) : undefined}
            eventHandlers={m.onClick ? { click: m.onClick } : undefined}
          >
            {m.popup && <Popup>{m.popup}</Popup>}
          </Marker>
        ))}

        {/* My Maps (imported Google KML/KMZ) points — rendered as small
            coloured circles to keep them visually distinct from artifact
            pins and to avoid cluttering the map with text labels. Personal
            points (manually created on this page) get edit/delete actions
            inside their popup. */}
        {myMapsPoints.map((p, i) => (
          <Marker
            key={`mm-${p.point_id || p.layer_id || "x"}-${p.lat}-${p.lng}-${i}`}
            position={[p.lat, p.lng]}
            icon={makeMyMapsPin(p.color || "#9C5B42")}
          >
            <Popup>
              <div className="min-w-[180px]">
                {p.layer_name && (
                  <div className="font-mono text-[10px] tracking-wider uppercase text-[#9C5B42] mb-1">
                    {p.layer_name}
                  </div>
                )}
                <div className="font-serif text-base text-[#1E2520]">{p.name || "Punto"}</div>
                {p.description && (
                  <div className="text-xs text-[#5C635A] mt-1 whitespace-pre-wrap">{p.description}</div>
                )}
                {p.is_personal && (p.onEdit || p.onDelete) && (
                  <div className="flex gap-1.5 mt-2.5 pt-2.5 border-t border-[#E5E0D8]">
                    {p.onEdit && (
                      <button
                        type="button"
                        onClick={p.onEdit}
                        className="text-[11px] text-[#9C5B42] hover:underline inline-flex items-center gap-1"
                        data-testid="mymaps-popup-edit"
                      >
                        Modifica
                      </button>
                    )}
                    {p.onDelete && (
                      <button
                        type="button"
                        onClick={p.onDelete}
                        className="text-[11px] text-red-700 hover:underline inline-flex items-center gap-1 ml-auto"
                        data-testid="mymaps-popup-delete"
                      >
                        Elimina
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {pickedPosition && !pickable && (
          <Marker position={pickedPosition} />
        )}
        {pickedPosition && pickable && (
          <Marker
            position={pickedPosition}
            icon={L.divIcon({
              className: "",
              html: `<div class="reperto-pin" style="background:#9C5B42">⊙</div>`,
              iconSize: [32, 32],
              iconAnchor: [16, 16],
            })}
          />
        )}
      </MapContainer>

      {showLayerSwitcher && (
        <LayerSwitcher
          baseId={baseId}
          setBaseId={setBaseId}
          overlayIds={overlayIds}
          setOverlayIds={setOverlayIds}
          opacities={opacities}
          setOpacity={setOpacity}
        />
      )}
    </div>
  );
}
